import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import confetti from "canvas-confetti";
import { Pencil, Trash2, CalendarDays, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { prettyDate } from "@/lib/geo";
import { GoalDialog } from "./GoalDialog";
import { CategoryBadge, categoryOf } from "@/lib/categories";

const Meta = ({ g, i }) => (
  g.done && g.completed_at ? (
    <span className="font-mono text-[11px] text-emerald-300/80 whitespace-nowrap" data-testid={`life-bucket-item-completed-${i}`}>Done · {prettyDate(g.completed_at)}</span>
  ) : !g.done && g.target_date ? (
    <span className="flex items-center gap-1.5 font-mono text-[11px] text-rose-300/80 whitespace-nowrap"><CalendarDays size={12} /> {prettyDate(g.target_date)}</span>
  ) : null
);

const celebrate = (el) => {
  const r = el.getBoundingClientRect();
  confetti({
    particleCount: 70, spread: 70, startVelocity: 32, scalar: 0.8, ticks: 160,
    origin: { x: (r.left + r.width / 2) / window.innerWidth, y: (r.top + r.height / 2) / window.innerHeight },
    colors: ["#39FF88", "#34D399", "#FB7185", "#FBBF24", "#7DD3FC"],
    disableForReducedMotion: true,
  });
};

export const GoalCard = ({ g, i, b, sortable, onMove, last }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: g.id, disabled: !sortable });
  return (
  <li ref={setNodeRef} className={`group relative flex items-start gap-4 py-5 px-2 sm:px-4 -mx-2 sm:-mx-4 rounded-xl hover:bg-white/[0.025] transition-colors duration-200 ${isDragging ? "bg-[#131A26] shadow-2xl z-10 relative" : "animate-rise"}`}
    style={{ animationDelay: `${Math.min(i, 12) * 30}ms`, transform: CSS.Transform.toString(transform), transition }} data-testid={`life-bucket-item-${i}`}>
    {sortable && (
      <button {...attributes} {...listeners} className="pt-1.5 -ml-1 text-slate-400 hover:text-white cursor-grab active:cursor-grabbing touch-none" data-testid={`life-drag-handle-${i}`} aria-label="Drag to reorder">
        <GripVertical size={16} />
      </button>
    )}
    <span className={`font-mono text-sm w-8 pt-1 shrink-0 ${g.done ? "text-emerald-400/70" : "text-rose-300/70"}`}>{String(i + 1).padStart(2, "0")}</span>
    <Checkbox checked={g.done} disabled={b.readOnly} onCheckedChange={(v) => {
        const el = document.querySelector(`[data-testid="life-bucket-item-checkbox-${i}"]`);
        if (v && el) celebrate(el);
        b.patchGoal(g.id, { done: !!v }, v ? "Done! One less thing on the list" : null);
      }}
      className="mt-1.5 h-5 w-5 shrink-0 rounded-full border-white/30 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500 data-[state=checked]:text-[#04120c]"
      data-testid={`life-bucket-item-checkbox-${i}`} />
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h4 className={`font-display text-xl sm:text-2xl leading-snug ${g.done ? "line-through decoration-emerald-400/60 text-slate-500" : ""}`} data-testid={`life-bucket-item-title-${i}`}>{g.title}</h4>
        {g.category && <CategoryBadge cat={categoryOf(g.category)} testId={`life-bucket-item-category-${i}`} />}
      </div>
      {g.notes && <p className="text-sm text-slate-400 mt-1.5 whitespace-pre-line max-w-2xl">{g.notes}</p>}
      <div className="sm:hidden mt-2"><Meta g={g} i={i} /></div>
    </div>
    <div className="hidden sm:block pt-2"><Meta g={g} i={i} /></div>
    {!b.readOnly && <div className="flex gap-1 pt-1 sm:opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
      {sortable && <>
        <button onClick={() => onMove(i, -1)} disabled={i === 0} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:pointer-events-none" data-testid={`life-move-up-${i}`} aria-label="Move up"><ArrowUp size={14} /></button>
        <button onClick={() => onMove(i, 1)} disabled={last} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:pointer-events-none" data-testid={`life-move-down-${i}`} aria-label="Move down"><ArrowDown size={14} /></button>
      </>}
      <GoalDialog goal={g} onSave={(f) => b.patchGoal(g.id, f, "Goal updated")} testId={`goal-edit-dialog-${i}`}>
        <button className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10" data-testid={`life-bucket-item-edit-${i}`} aria-label="Edit"><Pencil size={14} /></button>
      </GoalDialog>
      <button onClick={() => b.delGoal(g.id)} className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-white/10" data-testid={`life-bucket-item-delete-${i}`} aria-label="Delete"><Trash2 size={14} /></button>
    </div>}
  </li>
  );
};
